import { UseMutateAsyncFunction, UseMutationResult } from '@tanstack/react-query';
import classNames from 'classnames';
import { FormikProvider, useFormik } from 'formik';
import { useCallback, useId, useMemo } from 'react';
import * as yup from 'yup';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import { NAME_REGEX } from '@/modules/common/utils/regex.ts';
import { useAdminRegisterListQuery } from '@/modules/admin-dashboard/queries/registers.tsx';
import {
  BaseModalProps,
  Button,
  CustomSwitch,
  ModalContentWrapper,
  SelectButtons,
  SelectOption,
  SelectPaginate,
  SelectPaginateOption,
  TextInputField,
  TooltipNew,
  getSelectValueByEnum,
} from 'ui';
import {
  AddNewIssuerRequest,
  Issuer,
  IssuerAddNewIssuerRequest,
  IssuerAddNewIssuerResponse,
  IssuerPermission,
  KeycloakIssuerRole,
  RegisterParty,
  UpdateUserPermissionRequest,
  UpdateUserPermissionResponse,
} from 'api';

const validationSchema = yup.object<AddNewIssuerRequest>({
  first_name: yup
    .string()
    .trim()
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .min(2, 'Must be at least 2 characters')
    .required('This field is required'),
  last_name: yup
    .string()
    .trim()
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .min(2, 'Must be at least 2 characters')
    .required('This field is required'),
  email: yup.string().trim().email('Invalid email').required('Enter email'),
  register_ids: yup.array().min(1, 'This filed is required'),
  role: yup
    .mixed<KeycloakIssuerRole>()
    .oneOf(Object.values(KeycloakIssuerRole))
    .nullable()
    .optional(),
});

interface PermissionData {
  title: string;
  description: string;
  name: keyof Omit<IssuerPermission, 'register_id'>;
  implemented: boolean;
}

const permissionsData: PermissionData[] = [
  {
    title: 'Registry Information Edit',
    description:
      'Allows an Issuer to edit registry bank/ABN/contacts/distribution settings information',
    name: 'register_permission',
    implemented: true,
  },
  {
    title: 'Communications Validation',
    description:
      'Allows an Issuer to approve/decline/edit communications sent to investors by other issuers',
    name: 'communication_permission',
    implemented: false,
  },
  {
    title: 'Holder Details Edit',
    description: 'Allows an Issuer to edit/fill out KYC on behalf of investors',
    name: 'holder_details_permission',
    implemented: true,
  },
];

const rolesOptions: SelectOption<KeycloakIssuerRole>[] = [
  { value: KeycloakIssuerRole.SrIssuer, label: 'Issuer' },
  { value: KeycloakIssuerRole.SrIssuerAdmin, label: 'Issuer admin' },
];

interface IssuerCreateModalProps<T> extends BaseModalProps {
  isUpdating?: undefined;
  register?: RegisterParty;
  issuer?: Issuer;
  mutation: [
    (
      args: T,
    ) => UseMutationResult<IssuerAddNewIssuerResponse, unknown, IssuerAddNewIssuerRequest, unknown>,
    T,
  ];
}

interface IssuerPermissionsUpdateModalProps<T> extends BaseModalProps {
  isUpdating: true;
  register: RegisterParty;
  issuer: Omit<Issuer, 'is_email_confirmed'>;
  mutation: [
    (
      args: T,
    ) => UseMutationResult<
      UpdateUserPermissionResponse,
      unknown,
      UpdateUserPermissionRequest,
      unknown
    >,
    T,
  ];
}

type IssuerModalProps<T> = IssuerCreateModalProps<T> | IssuerPermissionsUpdateModalProps<T>;

const IssuerModal = <T,>({
  open,
  mutation,
  register,
  onClose,
  issuer,
  isUpdating,
}: IssuerModalProps<T>) => {
  const [mutationFn, mutationArgs] = mutation;

  const isEdit = !!issuer;

  const { fetchNextPage: fetchNextRegisterListPage } = useAdminRegisterListQuery(
    { page: 1, page_size: 15 },
    { cacheKey: useId() },
  );

  const initPermissions = useMemo(() => {
    if (!issuer || !issuer.user_permissions || !register) return null;

    return issuer.user_permissions.find(({ register_id }) => register_id === register.id);
  }, [issuer, register]);

  const initialValues = useMemo(
    (): AddNewIssuerRequest => ({
      first_name: issuer?.first_name ?? '',
      last_name: issuer?.last_name ?? '',
      email: issuer?.email ?? '',
      register_ids: register ? [register.id] : [],
      role: issuer && issuer.issuer_role ? issuer.issuer_role : KeycloakIssuerRole.SrIssuer,
      communication_permission: initPermissions?.communication_permission || false,
      holder_details_permission: initPermissions?.holder_details_permission || false,
      register_permission: initPermissions?.register_permission || false,
    }),
    [
      issuer,
      register,
      initPermissions?.communication_permission,
      initPermissions?.holder_details_permission,
      initPermissions?.register_permission,
    ],
  );

  const formik = useFormik<AddNewIssuerRequest>({
    initialValues,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { resetForm }) => {
      if (isUpdating) {
        await (
          mutateAsync as UseMutateAsyncFunction<
            UpdateUserPermissionResponse,
            unknown,
            UpdateUserPermissionRequest,
            unknown
          >
        )({
          user_id: issuer.id,
          register_id: register.id,
          role: values.role,
          register_permission: values.register_permission,
          communication_permission: values.communication_permission,
          holder_details_permission: values.holder_details_permission,
        });
      } else {
        await (
          mutateAsync as UseMutateAsyncFunction<
            IssuerAddNewIssuerResponse,
            unknown,
            IssuerAddNewIssuerRequest,
            unknown
          >
        )(values);
      }

      resetForm();
      onClose();
    },
    enableReinitialize: true,
  });

  const { mutateAsync, isLoading } = mutationFn({ ...mutationArgs, setErrors: formik.setErrors });

  const handleChangeRole = useCallback(
    (value: KeycloakIssuerRole) => {
      formik.setFieldValue('role', value);
      formik.setFieldError('role', '');
      if (value === KeycloakIssuerRole.SrIssuerAdmin) {
        formik.setFieldValue('register_permission', true);
        formik.setFieldValue('communication_permission', true);
        formik.setFieldValue('holder_details_permission', true);
      }
    },
    [formik],
  );

  const handleAsyncRegisterList = async (currentPage: number, search?: string) => {
    const response = await fetchNextRegisterListPage({ pageParam: { search, page: currentPage } });

    return response.data?.pages.reduce((acc: any, { data, page }) => {
      if (page !== currentPage) return acc;

      return data.map(({ name, id }) => ({
        label: name,
        value: { id, toString: () => name },
      }));
    }, []);
  };

  const handleChangeRegisters = (registers: SelectPaginateOption<{ id: number }>[]) => {
    const values = registers.map(({ value }) => value.id);
    formik.setFieldValue('register_ids', values);
  };

  const title = useMemo(() => {
    if (!register) return 'Add new issuer';

    return isEdit
      ? `Edit issuer permissions in "${register?.name}"`
      : `Add new issuer to "${register?.name}"`;
  }, [isEdit, register]);

  return (
    <ModalContentWrapper open={open} title={title} onClose={onClose}>
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col gap-8" onSubmit={preventFormDefault}>
          <div className="w-full flex gap-x-2 gap-y-8 flex-wrap lg:flex-nowrap align-middle justify-between">
            <TextInputField
              name="first_name"
              label="First Name"
              placeholder="First name..."
              disabled={isEdit}
            />
            <TextInputField
              name="last_name"
              label="Last Name"
              placeholder="Last name..."
              disabled={isEdit}
            />
          </div>
          <TextInputField name="email" label="email" placeholder="Email..." disabled={isEdit} />

          <SelectButtons
            label="Role"
            options={rolesOptions}
            selected={getSelectValueByEnum<KeycloakIssuerRole>(
              formik.values.role ?? '',
              KeycloakIssuerRole,
            )}
            onChange={handleChangeRole}
          />
          <div className="w-full flex flex-col gap-3">
            {permissionsData.map((permission, index) => (
              <TooltipNew
                key={index}
                text="This functionality has not yet been implemented"
                disabled={
                  formik.values.role === KeycloakIssuerRole.SrIssuerAdmin || permission.implemented
                }
                className={classNames({
                  hidden: !permission.implemented,
                })}
              >
                <CustomSwitch
                  variant="black"
                  disabled={
                    formik.values.role === KeycloakIssuerRole.SrIssuerAdmin ||
                    !permission.implemented
                  }
                  key={index}
                  checked={formik.values[permission.name]}
                  onChange={() => {
                    formik.setFieldValue(permission.name, !formik.values[permission.name]);
                  }}
                  className="text-[14px]"
                  label={permission.title}
                  subLabel={permission.description}
                />
              </TooltipNew>
            ))}
          </div>

          {!register && (
            <SelectPaginate
              isMulti
              label="REGISTER"
              error={
                Array.isArray(formik.errors.register_ids)
                  ? formik.errors.register_ids[0]
                  : formik.errors.register_ids
              }
              onAsync={handleAsyncRegisterList}
              onChange={handleChangeRegisters}
            />
          )}

          <Button onClick={formik.submitForm} loading={isLoading} disabled={isLoading}>
            Confirm
          </Button>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};

export default IssuerModal;
